import type {
  PaginatedData,
  ProductComponent,
  RepaymentPlanTemplate,
  SolarProduct,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { toQuery } from '../../lib/api-query';

export interface ProductListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  category?: string;
}

export interface PlanListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
}

export const catalogueApi = {
  listPublicProducts(filters: ProductListFilters = {}): Promise<PaginatedData<SolarProduct>> {
    return apiClient.get(`/public/solar-products${toQuery({ ...filters })}`);
  },

  listCustomerProducts(filters: ProductListFilters = {}): Promise<PaginatedData<SolarProduct>> {
    return apiClient.get(`/customer/products${toQuery({ ...filters })}`);
  },

  getPublicProduct(id: string): Promise<SolarProduct> {
    return apiClient.get(`/public/solar-products/${id}`);
  },

  getCustomerProduct(id: string): Promise<SolarProduct> {
    return apiClient.get(`/customer/products/${id}`);
  },

  listAdminProducts(filters: ProductListFilters = {}): Promise<PaginatedData<SolarProduct>> {
    return apiClient.get(`/admin/solar-products${toQuery({ ...filters })}`);
  },

  getAdminProduct(id: string): Promise<SolarProduct> {
    return apiClient.get(`/admin/solar-products/${id}`);
  },

  createProduct(input: {
    code: string;
    name: string;
    slug: string;
    description: string;
    category: string;
    systemCapacityKw: string;
    basePrice: string;
    warrantySummary?: string;
    isAvailable?: 'true' | 'false';
  }): Promise<SolarProduct> {
    return apiClient.post('/admin/solar-products', input);
  },

  updateProduct(
    id: string,
    input: {
      name?: string;
      description?: string;
      category?: string;
      systemCapacityKw?: string;
      basePrice?: string;
      warrantySummary?: string;
      status?: string;
      isAvailable?: 'true' | 'false';
    },
  ): Promise<SolarProduct> {
    return apiClient.patch(`/admin/solar-products/${id}`, input);
  },

  addProductComponent(
    productId: string,
    input: {
      componentType: string;
      componentName: string;
      description?: string;
      quantity?: number;
      unit?: string;
      modelSpecification?: string;
      warrantyMonths?: number;
      displayOrder?: number;
      isRequired?: 'true' | 'false';
    },
  ): Promise<ProductComponent> {
    return apiClient.post(`/admin/solar-products/${productId}/components`, input);
  },

  listCustomerRepaymentPlans(
    filters: PlanListFilters = {},
  ): Promise<PaginatedData<RepaymentPlanTemplate>> {
    return apiClient.get(`/customer/repayment-plans${toQuery({ ...filters })}`);
  },

  getCustomerRepaymentPlan(id: string): Promise<RepaymentPlanTemplate> {
    return apiClient.get(`/customer/repayment-plans/${id}`);
  },

  listRepaymentPlans(filters: PlanListFilters = {}): Promise<PaginatedData<RepaymentPlanTemplate>> {
    return apiClient.get(`/admin/repayment-plan-templates${toQuery({ ...filters })}`);
  },

  getRepaymentPlan(id: string): Promise<RepaymentPlanTemplate> {
    return apiClient.get(`/admin/repayment-plan-templates/${id}`);
  },

  createRepaymentPlan(input: {
    code: string;
    name: string;
    description?: string;
    minProductPrice?: string;
    maxProductPrice?: string;
    depositType: string;
    depositValue: string;
    tenureMonths: number;
    repaymentFrequency?: string;
  }): Promise<RepaymentPlanTemplate> {
    return apiClient.post('/admin/repayment-plan-templates', input);
  },

  updateRepaymentPlan(
    id: string,
    input: {
      name?: string;
      description?: string;
      minProductPrice?: string;
      maxProductPrice?: string;
      depositType?: string;
      depositValue?: string;
      tenureMonths?: number;
      status?: string;
    },
  ): Promise<RepaymentPlanTemplate> {
    return apiClient.patch(`/admin/repayment-plan-templates/${id}`, input);
  },
};

/** Returns active plans whose price band includes the given product price. */
export function filterPlansForProduct(
  plans: RepaymentPlanTemplate[],
  productPrice: string,
): RepaymentPlanTemplate[] {
  const priceCents = Math.round(Number.parseFloat(productPrice) * 100);

  if (Number.isNaN(priceCents)) {
    return plans;
  }

  return plans.filter((plan) => {
    if (plan.status !== 'ACTIVE') {
      return false;
    }

    if (plan.minProductPrice !== null) {
      const minCents = Math.round(Number.parseFloat(plan.minProductPrice) * 100);

      if (!Number.isNaN(minCents) && priceCents < minCents) {
        return false;
      }
    }

    if (plan.maxProductPrice !== null) {
      const maxCents = Math.round(Number.parseFloat(plan.maxProductPrice) * 100);

      if (!Number.isNaN(maxCents) && priceCents > maxCents) {
        return false;
      }
    }

    return true;
  });
}
